import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiPlus, FiTrash2, FiEdit3, FiCheck, FiX, FiEye, FiEyeOff, FiSave, FiShare2, FiCopy, FiLogOut, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import { toast } from 'react-toastify';
import axios from 'axios';
import ShareDialog from '../sharing/ShareDialog';
import IncomingShares from '../sharing/IncomingShares';
import { IS_STANDALONE } from '../../config/features';
import { sharesApi } from '../sharing/sharesApi';
import { formatApiError } from '../../utils/formatters';
import { useConfirm } from '../ConfirmDialog';
import LayoutSelector from './LayoutSelector';
import styles from './MonitoringDashboardModal.module.css';

/**
 * 실시간 대시보드 최상단 "⚙ 설정" 탭.
 * - 모든 비-설정 탭을 나열. 각 탭의 고정 그리드 레이아웃(1x1/1x2/2x1/2x2/1L2R/2L1R 또는 자동) 지정.
 * - 보이기/숨기기 토글 (설정 탭에서만 제어, 탭 바에서 감춘 탭은 숨김).
 * - kind='user' 인 사용자 정의 탭은 이름 변경·삭제 가능.
 * - 하단 "+ 탭 추가" / "설정 저장" 버튼.
 *
 * 파괴적 작업(삭제)은 OS 기본 confirm 대신 **인라인 2단계 확인** — 같은 행에서
 * "삭제?" 버튼이 한번 더 보이고, 10초 후 자동 취소. 토스트는 부모에서 표시.
 */

const getKindLabel = (t) => ({
  overview: t('monitoring:st.overview'),
  builtin: t('monitoring:st.builtin'),
  custom: t('monitoring:st.custom'),
  user: t('monitoring:st.user'),
  settings: t('monitoring:st.title'),
});
const KIND_CLASS = {
  overview: styles.kindOverview,
  builtin: styles.kindBuiltin,
  custom: styles.kindCustom,
  user: styles.kindUser,
};

const SettingsTab = ({
  tabs,
  onChangeLayout,
  onCreateTab,
  onDeleteTab,
  onRenameTab,
  onToggleHidden,
  onSaveAll,
  apiBase,      // `.../sql-developer` — 공유·복사(2026-10-03)
  onReload,     // 탭 목록 다시 읽기
  rotationList = [],          // 순환할 화면 — 순환 순서대로(2026-10-18)
  onToggleRotationInclude,    // (tabId) 순환에 넣기/빼기 — 탭 줄의 체크와 같은 동작
  onMoveRotation,             // (tabId, -1|+1) 순환 순서 바꾸기
}) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const shareApi = useMemo(() => sharesApi(apiBase), [apiBase]);
  const [shareTab, setShareTab] = useState(null);
  const copyTab = async (tab) => {
    try {
      await axios.post(`${apiBase}/monitoring/panels/tabs/${encodeURIComponent(tab.id)}/copy`, {});
      toast.success(t('monitoring:st.copied', { name: tab.name }));
      onReload && onReload();
    } catch (err) { toast.error(formatApiError(err, t)); }
  };
  const unsubscribe = async (tab) => {
    const ok = await confirm({ title: t('monitoring:st.unsubscribe'), message: t('monitoring:st.unsubscribeConfirm', { name: tab.name }), confirmText: t('monitoring:st.unsubscribe'), variant: 'danger' });
    if (!ok) return;
    try { await shareApi.remove(tab.shareId); onReload && onReload(); } catch (err) { toast.error(formatApiError(err, t)); }
  };
  // filter 콜백 변수명이 번역 t 를 가리지 않도록 tab 으로 쓴다.
  const rows = (tabs || []).filter(tab => tab && tab.kind !== 'settings');
  const rotationPos = new Map(rotationList.map((tab, i) => [tab.id, i]));
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [renameId, setRenameId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  // 인라인 삭제 확인: 한 번 클릭 → 해당 id 를 "대기" 상태로 표시. 10초 후 자동 취소.
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleBeginCreate = () => {
    setCreating(true);
    setNewName('');
  };
  const handleCancelCreate = () => {
    setCreating(false);
    setNewName('');
  };
  // 이름 중복 — 이 목록(내 탭 줄)에 같은 이름이 있으면 보내지 않는다(서버도 409 로 막는다, 2026-10-18). 공백·대소문자는 무시
  const nameKey = (n) => String(n || '').trim().replace(/\s+/g, ' ').toLowerCase();
  const nameTaken = (name, exceptId) => rows.some(tab => tab.id !== exceptId && nameKey(tab.name) === nameKey(name));
  const handleConfirmCreate = () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    if (nameTaken(trimmed, null)) { toast.warn(t('monitoring:st.nameTaken', { name: trimmed })); return; }
    onCreateTab?.(trimmed);
    setCreating(false);
    setNewName('');
  };

  const handleBeginRename = (tab) => {
    setRenameId(tab.id);
    setRenameValue(tab.name);
  };
  const handleConfirmRename = (tabId) => {
    const trimmed = renameValue.trim();
    if (trimmed && nameTaken(trimmed, tabId)) { toast.warn(t('monitoring:st.nameTaken', { name: trimmed })); return; }
    if (trimmed && trimmed !== rows.find(t => t.id === tabId)?.name) {
      onRenameTab?.(tabId, trimmed);
    }
    setRenameId(null);
    setRenameValue('');
  };
  const handleCancelRename = () => {
    setRenameId(null);
    setRenameValue('');
  };

  // 첫 클릭: 대기 상태로 전환. 다시 클릭하면 실제 삭제.
  // 다른 행 클릭/자동 타임아웃 시 대기 상태 해제.
  const handleDeleteClick = (tab) => {
    if (pendingDeleteId === tab.id) {
      onDeleteTab?.(tab.id);
      setPendingDeleteId(null);
      return;
    }
    setPendingDeleteId(tab.id);
    setTimeout(() => {
      setPendingDeleteId(prev => (prev === tab.id ? null : prev));
    }, 10000);
  };
  const cancelPendingDelete = () => setPendingDeleteId(null);

  const handleSaveClick = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await onSaveAll?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.settingsTab}>
      <h3>{t('monitoring:st.layoutSettings')}</h3>
      <p>
        {t('monitoring:st.layoutHint1')}
        {t('monitoring:st.layoutHint2')}
        {t('monitoring:st.userTabHint')}
      </p>

      {/* 구독·공유는 웹의 것 — 스탠드얼론은 혼자다(config/features.js IS_STANDALONE) */}
      {!IS_STANDALONE && <IncomingShares api={shareApi} kinds={['dashboard']} onAccepted={() => onReload && onReload()} />}
      <table className={styles.settingsTable}>
        <thead>
          <tr>
            <th style={{ width: '26%' }}>{t('monitoring:st.tabName')}</th>
            <th style={{ width: '10%' }}>{t('monitoring:st.kind')}</th>
            <th style={{ width: '8%' }}>{t('monitoring:st.show')}</th>
            <th style={{ width: '14%' }} title={t('monitoring:st.rotationHint')}>{t('monitoring:st.rotation')}</th>
            <th style={{ width: '28%' }}>{t('monitoring:st.layout')}</th>
            <th style={{ width: '14%' }}>{t('monitoring:st.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={6} style={{ color: '#6b7280', padding: 20, textAlign: 'center' }}>
                {t('monitoring:st.noTabs')}
              </td>
            </tr>
          )}
          {rows.map(tab => {
            const isOverview = tab.kind === 'overview';
            const isUser = tab.kind === 'user';
            const kindLabel = getKindLabel(t)[tab.kind] || tab.kind || '-';
            const kindClass = KIND_CLASS[tab.kind] || '';
            const renaming = renameId === tab.id;
            const pendingDelete = pendingDeleteId === tab.id;
            return (
              <tr
                key={tab.id}
                className={isOverview ? styles.settingsRowDisabled : ''}
                title={isOverview ? t('monitoring:st.overviewNoLayout') : ''}
              >
                <td>
                  {renaming ? (
                    <div className={styles.inlineEditGroup}>
                      <input
                        className={styles.inlineInput}
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleConfirmRename(tab.id);
                          if (e.key === 'Escape') handleCancelRename();
                        }}
                        autoFocus
                        maxLength={50}
                      />
                      <button
                        className={styles.iconBtnConfirm}
                        onClick={() => handleConfirmRename(tab.id)}
                        title={t('monitoring:st.save')}
                      >
                        <FiCheck size={14} />
                      </button>
                      <button
                        className={styles.iconBtn}
                        onClick={handleCancelRename}
                        title={t('monitoring:st.cancel')}
                      >
                        <FiX size={14} />
                      </button>
                    </div>
                  ) : (
                    <>
                      {tab.name}
                      {tab.subscribed && (
                        <span className={styles.subscribedBadge} title={t('monitoring:st.subscribedHint')}>
                          <FiShare2 size={10} /> {t('monitoring:st.subscribedFrom', { name: (tab.owner && tab.owner.name) || '' })}
                        </span>
                      )}
                    </>
                  )}
                </td>
                <td>
                  <span className={`${styles.tabKindBadge} ${kindClass}`}>{kindLabel}</span>
                </td>
                <td>
                  <button
                    type="button"
                    className={`${styles.visibilityToggle} ${tab.hidden ? styles.visibilityHidden : ''}`}
                    onClick={() => onToggleHidden?.(tab.id, !tab.hidden)}
                    disabled={tab.canEdit === false}
                    title={tab.hidden ? t('monitoring:st.hiddenClickToShow') : t('monitoring:st.visibleClickToHide')}
                    aria-pressed={!tab.hidden}
                  >
                    {tab.hidden ? <FiEyeOff size={15} /> : <FiEye size={15} />}
                  </button>
                </td>
                <td>
                  {/* 순환 — 넣기/빼기(탭 줄의 체크와 같다) + 순서. 숨긴 화면은 순환하지 않는다 */}
                  <div className={styles.rotationCell}>
                    <input
                      type="checkbox"
                      checked={!!tab.includeInRotation}
                      onChange={() => onToggleRotationInclude?.(tab.id)}
                      disabled={tab.hidden}
                      title={tab.hidden ? t('monitoring:st.rotationHidden') : t('monitoring:dash.rotateInclude')}
                      aria-label={t('monitoring:dash.rotateInclude')}
                    />
                    {rotationPos.has(tab.id) && (
                      <>
                        <span className={styles.rotationPos}>{t('monitoring:st.rotationPos', { n: rotationPos.get(tab.id) + 1 })}</span>
                        <button type="button" className={styles.iconBtn} onClick={() => onMoveRotation?.(tab.id, -1)}
                          disabled={rotationPos.get(tab.id) === 0} title={t('monitoring:st.rotationUp')} aria-label={t('monitoring:st.rotationUp')}>
                          <FiChevronUp size={13} />
                        </button>
                        <button type="button" className={styles.iconBtn} onClick={() => onMoveRotation?.(tab.id, 1)}
                          disabled={rotationPos.get(tab.id) === rotationList.length - 1} title={t('monitoring:st.rotationDown')} aria-label={t('monitoring:st.rotationDown')}>
                          <FiChevronDown size={13} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
                <td>
                  <LayoutSelector
                    value={tab.layout}
                    onChange={(next) => onChangeLayout(tab.id, next)}
                    disabled={isOverview || tab.canEdit === false}
                  />
                </td>
                <td>
                  {/* 구독한 대시보드·남의 탭: 내 것으로 복사 / 구독 해제. 내 대시보드: 공유 */}
                  {!renaming && !IS_STANDALONE && (
                    <div className={styles.rowActions}>
                      {tab.isMine && (
                        <button className={styles.iconBtn} onClick={() => setShareTab(tab)} title={t('monitoring:st.share')}>
                          <FiShare2 size={14} />
                        </button>
                      )}
                      {!tab.isMine && (
                        <button className={styles.iconBtn} onClick={() => copyTab(tab)} title={t('monitoring:st.copyToMine')}>
                          <FiCopy size={14} />
                        </button>
                      )}
                      {tab.subscribed && (
                        <button className={styles.iconBtnDanger} onClick={() => unsubscribe(tab)} title={t('monitoring:st.unsubscribe')}>
                          <FiLogOut size={14} />
                        </button>
                      )}
                    </div>
                  )}
                  {isUser && tab.canEdit !== false && !renaming && (
                    <div className={styles.rowActions}>
                      <button
                        className={styles.iconBtn}
                        onClick={() => handleBeginRename(tab)}
                        title={t('monitoring:st.rename')}
                      >
                        <FiEdit3 size={14} />
                      </button>
                      {pendingDelete ? (
                        <>
                          <button
                            className={styles.iconBtnConfirmDanger}
                            onClick={() => handleDeleteClick(tab)}
                            title={t('monitoring:st.confirmDelete')}
                          >
                            {t('monitoring:st.confirm')}
                          </button>
                          <button
                            className={styles.iconBtn}
                            onClick={cancelPendingDelete}
                            title={t('monitoring:st.cancel')}
                          >
                            <FiX size={14} />
                          </button>
                        </>
                      ) : (
                        <button
                          className={styles.iconBtnDanger}
                          onClick={() => handleDeleteClick(tab)}
                          title={t('monitoring:st.deleteTab')}
                        >
                          <FiTrash2 size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className={styles.settingsActions}>
        {creating ? (
          <div className={styles.inlineEditGroup}>
            <input
              className={styles.inlineInput}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleConfirmCreate();
                if (e.key === 'Escape') handleCancelCreate();
              }}
              placeholder={t('monitoring:st.newTabName')}
              maxLength={50}
              autoFocus
            />
            <button
              className={styles.btnPrimary}
              onClick={handleConfirmCreate}
              disabled={!newName.trim()}
            >
              {t('monitoring:st.add')}
            </button>
            <button className={styles.btnSecondary} onClick={handleCancelCreate}>
              {t('monitoring:st.cancel')}
            </button>
          </div>
        ) : (
          <>
            <button className={styles.btnPrimary} onClick={handleBeginCreate}>
              <FiPlus size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              {t('monitoring:st.addTab')}
            </button>
            <button
              className={styles.btnPrimary}
              onClick={handleSaveClick}
              disabled={saving}
              title={t('monitoring:st.saveAllHint')}
            >
              <FiSave size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              {saving ? t('monitoring:st.saving') : t('monitoring:st.saveSettings')}
            </button>
          </>
        )}
      </div>
      <ShareDialog
        open={!!shareTab}
        onClose={() => setShareTab(null)}
        title={t('monitoring:st.shareTitle', { name: shareTab ? shareTab.name : '' })}
        api={shareApi}
        ownerUsrid={shareTab && shareTab.ownerUsrid}
        hint={t('monitoring:st.shareHint')}
        load={() => (shareTab ? shareApi.dashShares(shareTab.id) : Promise.resolve([]))}
        submit={(usrids) => shareApi.shareDash(shareTab.id, { toUsrids: usrids })}
      />
    </div>
  );
};

export default SettingsTab;
